import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import { basicService, authService } from '@app/services';
import { store, sessionStore } from '@app/helpers';
import { loadingData, unhandledException } from '@app/store/actions';
import { SessionStorageTypes } from '@app/models';
import { AUTHORIZE_TOKEN_API_PATH, SIGNOUT_API_PATH } from './auth.service';

declare module 'axios' {
    export interface AxiosRequestConfig {
        local?: boolean;
        dirSinc?: boolean;
        onError?: Function;
        onSuccess?: Function;
    }
}

export interface ISuccessErrorResponse {
    isSuccess: boolean;
    errorResponse?: AxiosResponse;
}

const axiosInstance: AxiosInstance = axios.create();

axiosInstance.interceptors.request.use(
    (config: AxiosRequestConfig) => new Promise((resolve, reject) => {
        
        function SetBaseUrl(fullDomain: string): void {

            // https is only used when running dev build using the command:
            // > npm start
            let protocol = process.env.NODE_ENV === 'development' ? 'http://' : 'https://';
            config.baseURL = protocol + fullDomain + '/';
        }

        function SetHeaders(realm: string): void {            
            if (!config.headers) {
                config.headers = {};
            }

            const sid = sessionStore.getItem(SessionStorageTypes.aaSid);
            if (!!sid) config.headers['x-aa-session-fixation'] = sid;

            const correlation = sessionStore.getItem(SessionStorageTypes.correlationId);
            if (correlation) config.headers['x-correlation-id'] = correlation;

            config.headers['Cache-Control'] = 'no-cache';
            config.headers['Pragma'] = 'no-cache';
            config.withCredentials = true;

            // used only when running dev build using the command:
            // > npm start
            if (process.env.NODE_ENV === 'development' && realm !== 'localhost') {
                config.headers['x-homerealm'] = realm;
            }
        }
        
        basicService.getHomeRealm((domain, realm, noCache) => {
            let fullDomain = '';

            if (noCache) {
                fullDomain = domain;
            }
            else {
                // used only when running dev build using the command:
                // > npm start
                if (process.env.NODE_ENV === 'development') {
                    if (process.env.REACT_APP_PASSLY_API_LOCAL_PROXY) {
                        fullDomain = process.env.REACT_APP_PASSLY_API_LOCAL_PROXY;
                    }
                    else {
                        throw 'Check REACT_APP_PASSLY_API_LOCAL_PROXY is set in .env.development file';
                    }
                }
                else if (config.local) {
                    fullDomain = domain;
                }
                else if (realm) {
                    let parentDomain = domain.substring(domain.indexOf('.'));
                    fullDomain = realm + parentDomain;
                }
            }

            SetBaseUrl(fullDomain);
            SetHeaders(realm);
        });

        if (!config.url?.endsWith(AUTHORIZE_TOKEN_API_PATH) && !config.url?.endsWith(SIGNOUT_API_PATH) && !window.location.pathname.endsWith('/signin') && authService.tokenExpiresSoonish()) {
            // Try to get a new access token using the existing refresh token before sending this request.
            // If we fail to get a new access token it will still try the request using the old token.
            authService.getNewAccessTokenOnRequest().then(() => { resolve(config); }).catch(() => resolve(config));
        } else {
            resolve(config);
        }
    }),
    (error: AxiosError<any>) => {
        return Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        setResponseTracing(response);

        if (response.config.onSuccess !== undefined) {
            response.config.onSuccess(response);
        }

        return response;
    },
    (error: AxiosError) => {
        if (error.response !== undefined) {
            setResponseTracing(error.response);

            const {
                config,
                response,
                response: { status }
            } = error;
            const originalRequest = config;
            const { dispatch } = store;

            if (status === 401 && !originalRequest?.url?.endsWith(SIGNOUT_API_PATH)) {

                if (!!response && typeof response.headers.length) {
                    const sessionFixation = response.headers['x-aa-session-fixation'];

                    if (sessionFixation) {
                        sessionStore.setItem(SessionStorageTypes.aaSid, sessionFixation);
                    }
                }

                dispatch(loadingData(false));
                basicService.redirect('/signin');
                return Promise.reject(error);
            } else if (status === 403) {
                dispatch(unhandledException({ detail: { message: 'ACCESS_DENIED' } }));
            } else {
                let response: any = generateErrorMessage(error, status);

                if (basicService.handleExceptions()) {
                    // TODO: Uncomment when we will set up dev variable
                    // console.log(JSON.stringify(response));
                } else if (!originalRequest?.url?.endsWith(AUTHORIZE_TOKEN_API_PATH)) {
                    const { dispatch } = store;
                    dispatch(unhandledException(response));
                }
            }

            if (error.config?.onError !== undefined) {
                error.config?.onError(error);
            }
        }
        return Promise.reject(error);
    }
);

function generateErrorMessage(error: any, status: number): any {
    let response: any = {
        message: 'Response could not be parsed',
        statusCode: status,
        url: error.request.responseURL
    };

    let contentType: string = error.response.headers['content-type'];

    if (contentType && contentType.startsWith('text/html')) {
        response.type = 'text/html';
        response.message = error.response.data;
    } else if (contentType && contentType.startsWith('text')) {
        response.type = 'text';
        response.message = error.response.data;
    } else if (contentType && contentType.startsWith('application/json')) {
        response.type = 'object';
        if (error.response.data.error !== undefined) {
            response.message = error.response.data.error;
        } else {
            response.message = error.response.data;
        }
    } else {
        try {
            // if the server is down when the request hits it
            if (status === 0 && error.response.data === '') {
                response.message = 'Failed to contact server';
            } else {
                response.message = JSON.parse(error.response.data);
            }
        } catch (e) {
            response.message = error.response.data;
        }
    }

    response.correlationId = sessionStore.getItem(SessionStorageTypes.correlationId) || '';

    return response;
}

function setResponseTracing(axiosResponse: AxiosResponse): void {
    let correlationId = '';

    try {
        correlationId = axiosResponse.headers['x-correlation-id'] || '';
    } catch (e) {
        // TODO: Uncomment when we will set up dev variable
        // console.error(e);
    }

    if (correlationId !== '') {
        sessionStore.setItem(SessionStorageTypes.correlationId, correlationId);
    }
}

export const apiService = axiosInstance;
